// import React from 'react';
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'; 
import { LineChart, Line, BarChart, Bar, PieChart, Pie, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell, ReferenceLine, Area, ComposedChart, ReferenceArea} from 'recharts';
import Plot from 'react-plotly.js';
import { TrendingUp, Calendar, Network, BarChart3, X, Info, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import { API_URL } from '../config';
import InfoPopup from './InfoPopup';
import { vizNarratives } from '../content/vizNarratives';
import { useAskAnalyst } from '../context/AskAnalystContext';

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  const headers = {
    'Content-Type': 'application/json',
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  return headers;
};
// import ReactMarkdown from 'react-markdown';

const Card = ({ children, className = '' }) => {
  return (
    <div className={`bg-white border border-mongodb-gray-light2 rounded-2xl shadow-md ${className}`}>
      {children}
    </div>
  );
};

const CardHeader = ({ children, className = '' }) => {
  return (
    <div className={`p-6 pb-4 ${className}`}>
      {children}
    </div>
  );
};

const CardTitle = ({ children, className = '' }) => {
  return (
    <h3 className={`text-lg font-semibold ${className}`}>
      {children}
    </h3>
  );
};

const CardContent = ({ children, className = '' }) => {
  return (
    <div className={`p-6 pt-0 ${className}`}>
      {children}
    </div>
  );
};

const ExampleTableModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;
  
  const exampleData = {
    totalSales: 545,
    components: [
      {
        name: 'Baseline',
        value: 648,
        percentage: 67.2,
        subComponents: [
          { name: 'Intercept', value: 432, description: 'Base level sales' },
          { name: 'Trend', value: 216, description: 'Growth over time' }
        ]
      },
      {
        name: 'Media',
        value: 105,
        percentage: 11.0,
        subComponents: [
          { name: 'Google Search', value: 83, percentage: 79.1, description: 'Primary performance channel' },
          { name: 'Bing Search', value: 15, percentage: 14.5, description: 'Secondary search channel' },
          { name: 'LinkedIn', value: 2.4, percentage: 2.3, description: 'B2B professional targeting' },
          { name: 'Facebook', value: 2.3, percentage: 2.2, description: 'Social engagement' },
          { name: 'GDN', value: 2.0, percentage: 1.9, description: 'Display awareness' }
        ]
      },
      {
        name: 'Macro Economic Factors',
        value: -119,
        percentage: -21.8,
        description: 'Economic headwinds'
      },
      {
        name: 'Seasonality',
        value: 0,
        percentage: 0.0,
        description: 'Minimal seasonal patterns'
      }
    ],
    insights: [
      'Strong Organic Foundation: 67% of sales come from baseline, indicating a healthy business that doesn\'t rely solely on advertising',
      'Marketing Efficiency: Media contributes 11% incrementally, showing clear Conversion Efficiency on marketing spend',
      'Channel Performance: Google Search dominates media contribution (79% of marketing impact), Bing provides meaningful secondary search contribution (15%), Social/Display channels (Facebook, LinkedIn, GDN) contribute ~6% combined',
      'External Challenges: Economic factors create a -22% headwind, meaning the business is performing well despite adverse conditions'
    ]
  };
  
  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-6 z-50"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-mongodb-green-dark2 text-white p-4 rounded-t-lg flex items-center justify-between">
          <h3 className="text-lg font-bold flex items-center gap-2">
            <Info className="w-5 h-5" />
            Example: Decomposing Weekly Sales (545 Units)
          </h3>
          <button
            onClick={onClose}
            className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-1 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="p-6">
          <p className="text-mongodb-gray-dark3 mb-4">
            Based on the Global model's average performance across 171 weeks, here's how 545 units of weekly sales break down:
          </p>
          
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-gray-200 rounded-lg overflow-hidden">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-200 p-3 text-left font-semibold text-mongodb-gray-dark3">Component</th>
                  <th className="border border-gray-200 p-3 text-center font-semibold text-mongodb-gray-dark3">Value (Units)</th>
                  <th className="border border-gray-200 p-3 text-center font-semibold text-mongodb-gray-dark3">Percentage</th>
                  <th className="border border-gray-200 p-3 text-left font-semibold text-mongodb-gray-dark3">Description</th>
                </tr>
              </thead>
              <tbody>
                {exampleData.components.map((component, idx) => (
                  <React.Fragment key={idx}>
                    <tr className={`${idx % 2 === 0 ? 'bg-white' : 'bg-mongodb-gray-light3'} hover:bg-mongodb-green-dark2 hover:bg-opacity-5`}>
                      <td className="border border-gray-200 p-3 font-bold text-mongodb-gray-dark3">{component.name}</td>
                      <td className="border border-gray-200 p-3 text-center font-bold text-mongodb-gray-dark3">{component.value}</td>
                      <td className="border border-gray-200 p-3 text-center font-bold text-mongodb-gray-dark3">{component.percentage}%</td>
                      <td className="border border-gray-200 p-3 text-gray-700">{component.description || 'Your organic business foundation'}</td>
                    </tr>
                    {component.subComponents && component.subComponents.map((sub, subIdx) => (
                      <tr key={`${idx}-${subIdx}`} className={`${idx % 2 === 0 ? 'bg-white' : 'bg-mongodb-gray-light3'}`}>
                        <td className="border border-gray-200 p-3 pl-8 text-gray-600">↳ {sub.name}</td>
                        <td className="border border-gray-200 p-3 text-center text-gray-600">{sub.value}</td>
                        <td className="border border-gray-200 p-3 text-center text-gray-600">
                          {sub.percentage ? `${sub.percentage}% of ${component.name.toLowerCase()}` : '-'}
                        </td>
                        <td className="border border-gray-200 p-3 text-gray-600">{sub.description}</td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
                <tr className="bg-mongodb-green-dark2 bg-opacity-10 font-bold">
                  <td className="border border-gray-200 p-3 font-bold text-mongodb-gray-dark3">Total</td>
                  <td className="border border-gray-200 p-3 text-center font-bold text-mongodb-gray-dark3">{exampleData.totalSales}</td>
                  <td className="border border-gray-200 p-3 text-center font-bold text-mongodb-gray-dark3">100%</td>
                  <td className="border border-gray-200 p-3 text-gray-700">Actual weekly sales</td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <div className="mt-6 bg-mongodb-green-dark1 bg-opacity-10 border-l-4 border-mongodb-green-dark2 p-4 rounded">
            <h4 className="text-mongodb-green-dark2 font-bold mb-3">Key Insights from This Example:</h4>
            <ul className="space-y-2 text-mongodb-gray-dark3">
              {exampleData.insights.map((insight, idx) => (
                <li key={idx} className="flex items-start">
                  <span className="mr-2 text-mongodb-green-dark2 font-bold">{idx + 1}.</span>
                  <span>{insight}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

// MongoDB Official Brand Color Palette - Highly Distinctive Colors for Charts
// Using different colors (not just shades) for clear visual distinction
const colors = [
  '#3D4F58', // 0: MongoDB Grey dark2 - for Media (contribution chart)
  '#083C90', // 1: MongoDB Blue dark2 - for Seasonality (contribution chart)
  '#5E0C9E', // 2: MongoDB Purple dark2 - for Baseline (contribution chart)
  '#00684A', // 3: MongoDB Green dark2 - for Inflation & Unemployment (contribution chart)
  '#DB3030', // 4: MongoDB Red - LinkedIn Organic (contribution chart)
  '#00A35C', // 5: MongoDB Medium Green - Secondary green
  '#0498EC', // 6: MongoDB Sky Blue - Light blue variant
  '#FF6960', // 7: MongoDB Coral/Light Red - Warm red
  '#71F6BA', // 8: MongoDB Mint Green - Light green accent
  '#FFC010', // 9: MongoDB Gold/Yellow - Warm accent
  '#5E0C9E', // 10: MongoDB Deep Purple - Dark purple
  '#944F01', // 11: MongoDB Brown/Dark Gold - Earthy tone
  // Extra bars: only hexes from `theme.extend.colors.mongodb` in tailwind.config.js
  // Last three positions (12–14) use the official green ramp (avoid green-dark3 #023430 — reads as black on charts)
  '#00684A', // 12: mongodb green-dark2
  '#00ED64', // 13: mongodb green-base
  '#00A35C', // 14: mongodb green-dark1
  '#1254B7', // 15: mongodb blue-dark1
  '#016BF8', // 16: mongodb blue-base
  '#5E0C9E', // 17: mongodb purple-dark2
  '#B45AF2', // 18: mongodb purple-base
  '#2D0B89', // 19: mongodb purple-dark3
];

// Styled components using Tailwind
const Slide = ({ children, title, subtitle }) => (
  <div className="bg-white rounded-2xl shadow-md mb-6 p-6 border border-mongodb-gray-light2">
    <div className="border-b-2 border-mongodb-gray-light2 pb-4 mb-6">
      <h1 className="text-xl font-bold text-mongodb-gray-dark3">{title}</h1>
      {subtitle && <p className="text-mongodb-gray-dark1 mt-2">{subtitle}</p>}
    </div>
    {children}
  </div>
);

const MetricCard = ({ value, label, highlight }) => (
  <div className="bg-white p-4 rounded-2xl text-center border border-mongodb-gray-light2 shadow-md">
    <h3 className={`text-2xl font-bold mb-1 ${highlight ? 'text-mongodb-green-dark2' : 'text-mongodb-gray-dark3'}`}>
      {value}
    </h3>
    <p className="text-mongodb-gray-dark1 text-sm">{label}</p>
  </div>
);

// Helper function to adjust dates to week boundaries
const adjustToWeekBoundary = (dateStr, type) => {
  if (!dateStr || dateStr === 'N/A') return dateStr;
  
  const date = new Date(dateStr);
  const dayOfWeek = date.getDay(); // 0 = Sunday, 1 = Monday, etc.
  
  if (type === 'historical_end') {
    // Adjust to next Sunday (end of week)
    const daysUntilSunday = dayOfWeek === 0 ? 0 : 7 - dayOfWeek;
    date.setDate(date.getDate() + daysUntilSunday);
  } else if (type === 'forecast_start') {
    // Adjust to previous Monday (start of week)
    const daysSinceMonday = dayOfWeek === 1 ? 0 : (dayOfWeek === 0 ? 6 : dayOfWeek - 1);
    date.setDate(date.getDate() - daysSinceMonday);
  }
  
  // Format as YYYY-MM-DD
  return date.toISOString().split('T')[0];
};

// Custom tick component for multi-line labels
const CustomizedAxisTick = (props) => {
  const { x, y, payload } = props;
  
  // Split text at underscores or after certain length
  const splitText = (text) => {
    if (!text) return [''];
    
    // Split by underscore
    const parts = text.split('_');
    
    // If too many parts, group them
    if (parts.length > 2) {
      return [
        parts.slice(0, 2).join('_'),  // First line
        parts.slice(2).join('_')       // Second line
      ];
    }
    
    return parts;
  };
  
  const lines = splitText(payload.value);
  
  return (
    <g transform={`translate(${x},${y})`}>
      <text 
        x={0} 
        y={0} 
        dy={50}
        textAnchor="middle" 
        fill="#666"
        fontSize={14}
        fontWeight="bold"
      >
        {lines.map((line, index) => (
          <tspan 
            x={0} 
            dy={index === 0 ? 0 : 13} 
            key={index}
          >
            {line}
          </tspan>
        ))}
      </text>
    </g>
  );
};

const TimeframeBar = ({ comprehensiveData, periods }) => {
  // Use periods data if available, otherwise fallback to calculating from comprehensiveData
  let historicalStart, historicalEnd, forecastStart, forecastEnd;
  
  if (periods && periods.historical_start) {
    // Use actual dates from periods
    historicalStart = periods.historical_start;
    historicalEnd = adjustToWeekBoundary(periods.historical_end, 'historical_end');
    forecastStart = adjustToWeekBoundary(periods.forecast_start, 'forecast_start');
    forecastEnd = periods.forecast_end;
  } else {
    // Fallback: Calculate from comprehensiveData
    const historicalWeeks = comprehensiveData.filter(d => d.period === 'historical');
    const forecastWeeks = comprehensiveData.filter(d => d.period === 'forecast');
    
    historicalStart = historicalWeeks.length > 0 ? historicalWeeks[0].date : 'N/A';
    historicalEnd = historicalWeeks.length > 0 ? adjustToWeekBoundary(historicalWeeks[historicalWeeks.length - 1].date, 'historical_end') : 'N/A';
    forecastStart = forecastWeeks.length > 0 ? adjustToWeekBoundary(forecastWeeks[0].date, 'forecast_start') : 'N/A';
    forecastEnd = forecastWeeks.length > 0 ? forecastWeeks[forecastWeeks.length - 1].date : 'N/A';
  }

  return (
    <div className="bg-gray-200 h-12 flex items-center justify-center rounded-2xl my-4 font-semibold">
      <div className="flex-1 text-center bg-mongodb-american-green bg-opacity-20 h-full flex items-center justify-center border-r-2 border-dashed border-mongodb-american-green">
        <span className="text-mongodb-arsenic">Historical: ({historicalStart} - {historicalEnd})</span>
      </div>
      <div className="flex-1 text-center bg-orange-400 bg-opacity-20 h-full flex items-center justify-center">
        <span className="text-mongodb-gray-dark3">Forecast: ({forecastStart} - {forecastEnd})</span>
      </div>
    </div>
  );
};

// Channel → motion (Self serve / Sales sold / Brand) per locked 9-channel taxonomy
const CHANNEL_MOTION = {
  'Google Brand': 'Self serve',
  'Google Non-Brand': 'Self serve',
  'Bing Brand': 'Self serve',
  'Bing Non-Brand': 'Self serve',
  'LinkedIn': 'Self serve',
  'Meta': 'Self serve',
  'Display': 'Self serve',
  'YouTube': 'Self serve',
  'All Channels': 'Sales sold',
};

const AttributionTable = ({ channelAttribution, estimatedPtsFullyMatured = {} }) => {
  // Handle empty state
  if (!channelAttribution || channelAttribution.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-mongodb-green-dark2 mb-4"></div>
        <p className="text-mongodb-gray-dark3 text-sm">Loading channel attribution data...</p>
      </div>
    );
  }

  return (
  <div className="overflow-x-auto mt-6">
    <table className="w-full border-collapse border border-gray-200 rounded-2xl overflow-hidden">
      <thead>
        <tr className="bg-gray-100">
          <th className="border border-gray-200 p-3 text-left font-semibold text-mongodb-gray-dark3">Channel</th>
          <th className="border border-gray-200 p-3 text-center font-semibold text-mongodb-gray-dark3">Motion</th>
          <th className="border border-gray-200 p-3 text-center font-semibold text-mongodb-gray-dark3">Actual Media Spend</th>
          <th className="border border-gray-200 p-3 text-center font-semibold text-mongodb-gray-dark3">Actual PT's</th>
          <th className="border border-gray-200 p-3 text-center font-semibold text-mongodb-gray-dark3">
            <div className="flex flex-col">
              <span className="text-xs">Estimated PT’s</span>
              <span className="text-xs">Fully Matured</span>
            </div>
          </th>
          <th className="border border-gray-200 p-3 text-center font-semibold text-mongodb-gray-dark3">Cost per Actual PT</th>
          <th className="border border-gray-200 p-3 text-center font-semibold text-mongodb-gray-dark3">% of Total Impact</th>
          <th className="border border-gray-200 p-3 text-center font-semibold text-mongodb-gray-dark3">Ad Effect Duration (Half-life)</th>
        </tr>
      </thead>
      <tbody>
        {channelAttribution.map((channel, index) => (
          <tr key={index} className={`hover:bg-mongodb-gray-light3 ${index % 2 === 0 ? 'bg-white' : 'bg-mongodb-gray-light3'}`}>
            <td className="border border-gray-200 p-3 font-medium text-mongodb-gray-dark3">{channel.name}</td>
            <td className="border border-gray-200 p-3 text-center text-gray-700">{CHANNEL_MOTION[channel.name] || '—'}</td>
            <td className="border border-gray-200 p-3 text-center text-gray-700">{channel.spend}</td>
            <td className="border border-gray-200 p-3 text-center text-gray-700">{channel.revenue}</td>
            <td className="border border-gray-200 p-3 text-center text-gray-700">
              {(channel.estimated_pts_fully_matured ?? 0).toLocaleString('en-US')}
            </td>
            <td className="border border-gray-200 p-3 text-center font-bold text-black">
              ${channel.cost_per_pt ? Math.round(parseFloat(channel.cost_per_pt)).toLocaleString('en-US') : '0'}
            </td>
            <td className="border border-gray-200 p-3 text-center text-gray-700">{channel.impact}</td>
            <td className="border border-gray-200 p-3 text-center text-gray-700">{channel.adstock}</td>
          </tr>
        ))}
        {/* Total Row */}
        {(() => {
          const totals = channelAttribution.reduce((acc, channel) => {
            // Use raw unrounded spend value for accurate totals
            acc.totalSpend += channel.spend_raw || 0;
            
            // Sum incremental PTs (remove commas, convert to number)
            const revenueValue = parseFloat(channel.revenue.replace(/[,]/g, '')) || 0;
            acc.totalRevenue += revenueValue;
            
            // Sum % of total impact (remove %, convert to number)
            const impactValue = parseFloat(channel.impact.replace(/%/g, '')) || 0;
            acc.totalImpact += impactValue;
            
            acc.totalEstimatedPtsFullyMatured += (channel.estimated_pts_fully_matured ?? 0);
            
            return acc;
          }, {
            totalSpend: 0,
            totalRevenue: 0,
            totalImpact: 0,
            totalEstimatedPtsFullyMatured: 0
          });
          
          const avgCostPerPT = totals.totalRevenue > 0
            ? totals.totalSpend / totals.totalRevenue
            : 0;

          return (
            <tr className="bg-gray-100 font-bold">
              <td className="border border-gray-200 p-3 font-semibold text-mongodb-gray-dark3">Total</td>
              <td className="border border-gray-200 p-3 text-center text-gray-700">-</td>
              <td className="border border-gray-200 p-3 text-center text-gray-700">
                ${Math.round(totals.totalSpend).toLocaleString('en-US')}
              </td>
              <td className="border border-gray-200 p-3 text-center text-gray-700">
                {totals.totalRevenue.toLocaleString('en-US', {minimumFractionDigits: 0, maximumFractionDigits: 0})}
              </td>
              <td className="border border-gray-200 p-3 text-center text-gray-700">
                {totals.totalEstimatedPtsFullyMatured.toLocaleString('en-US')}
              </td>
              <td className="border border-gray-200 p-3 text-center font-bold text-black">
                ${Math.round(avgCostPerPT).toLocaleString('en-US')}
              </td>
              <td className="border border-gray-200 p-3 text-center text-gray-700">
                {Math.round(totals.totalImpact)}%
              </td>
              <td className="border border-gray-200 p-3 text-center text-gray-700">
                -
              </td>
            </tr>
          );
        })()}
      </tbody>
    </table>
  </div>
);
};

export const OptimizationTable = ({ optimization, region, quarter, onMetricsUpdate, isCurrentQuarter = true }) => {
  const [budgetInputs, setBudgetInputs] = useState({});
  const [recalculatedMetrics, setRecalculatedMetrics] = useState({});
  const [isRecalculating, setIsRecalculating] = useState({});
  const [incBudget, setIncBudget] = useState(0);
  const [incBudgetText, setIncBudgetText] = useState("0");
  const [hasPendingEdits, setHasPendingEdits] = useState(false);
  const budgetInputsRef = useRef({});
  const committedBudgetsRef = useRef({});

  const parseRecommended = useCallback((opt) => {
    if (typeof opt.recommended === 'number') return opt.recommended;
    return parseFloat(String(opt.recommended || '').replace(/[^0-9.]/g, '')) * 1e3 || 0;
  }, []);

  const toNumber = useCallback((value) => {
    if (typeof value === 'string') return parseFloat(value) || 0;
    return Number(value) || 0;
  }, []);

  // Reset / initialize when region or quarter changes (not on every optimization array identity)
  useEffect(() => {
    const initialInputs = {};
    optimization.forEach((opt) => {
      initialInputs[opt.name] = parseRecommended(opt);
    });
    setBudgetInputs(initialInputs);
    committedBudgetsRef.current = initialInputs;
    setRecalculatedMetrics({});
    setIsRecalculating({});
    setIncBudget(0);
    setIncBudgetText("0");
    setHasPendingEdits(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- remount via key on region/quarter; avoid reset on sorted-array identity
  }, [region, quarter, parseRecommended]);

  useEffect(() => {
    budgetInputsRef.current = budgetInputs;
  }, [budgetInputs]);

  // Notify parent when metrics or budgets change
  useEffect(() => {
    if (onMetricsUpdate) {
      onMetricsUpdate(recalculatedMetrics, budgetInputs);
    }
  }, [recalculatedMetrics, budgetInputs, onMetricsUpdate]);

  const recalculateMetrics = useCallback(async (channel, budget) => {
    const budgetNum = typeof budget === 'string' ? parseFloat(budget) || 0 : Number(budget) || 0;

    if (budgetNum < 0) {
      setRecalculatedMetrics((prev) => ({
        ...prev,
        [channel]: { expected_PTs: 0, cost_per_PT: 0, saturation_pct: 0, headroom: 0 },
      }));
      setIsRecalculating((prev) => ({ ...prev, [channel]: false }));
      return;
    }

    const channelOpt = optimization.find((opt) => opt.name === channel);
    const channelTotalBudget = channelOpt
      ? parseFloat(String(channelOpt.total_budget).replace(/[^0-9.]/g, ''))
      : 0;
    if (budgetNum === 0 && channelTotalBudget === 0) {
      setRecalculatedMetrics((prev) => ({
        ...prev,
        [channel]: { expected_PTs: 0, cost_per_PT: 0, saturation_pct: 0, headroom: 0 },
      }));
      setIsRecalculating((prev) => ({ ...prev, [channel]: false }));
      return;
    }

    setIsRecalculating((prev) => ({ ...prev, [channel]: true }));

    try {
      const requestBody = {
        channel,
        budget: budgetNum,
        region,
        quarter,
      };

      const response = await fetch(`${API_URL}/api/recalculate-metrics`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();
      if (data.error) {
        console.error('Recalculation error:', data.error);
        toast.error(data.error);
        return;
      }

      setRecalculatedMetrics((prev) => ({
        ...prev,
        [channel]: data,
      }));
    } catch (error) {
      console.error('Error recalculating metrics:', error);
      toast.error("We couldn't update those metrics. Please try again.");
    } finally {
      setIsRecalculating((prev) => ({ ...prev, [channel]: false }));
    }
  }, [region, quarter, optimization]);

  // Full re-optimization: add incBudget to the current remaining-budget pool
  // and reallocate optimally across every channel (powers "Incremental budget").
  const handleReoptimize = useCallback(async () => {
    const allNames = optimization.map((opt) => opt.name);
    setIsRecalculating((prev) => {
      const next = { ...prev };
      allNames.forEach((name) => { next[name] = true; });
      return next;
    });

    try {
      const response = await fetch(`${API_URL}/api/reoptimize-budget`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ incremental_budget: incBudget, region, quarter }),
      });

      const data = await response.json();
      if (data.error) {
        toast.error(data.error);
        return;
      }

      const newBudgetInputs = {};
      const newMetrics = {};
      const newCommitted = {};
      (data.channels || []).forEach((row) => {
        newBudgetInputs[row.name] = row.recommended;
        newCommitted[row.name] = row.recommended;
        newMetrics[row.name] = {
          expected_PTs: row.expected_PTs_recommeneded,
          cost_per_PT: row.cost_per_PT,
          saturation_pct: row.saturation_pct,
          headroom: row.headroom,
        };
      });

      setBudgetInputs(newBudgetInputs);
      committedBudgetsRef.current = newCommitted;
      setRecalculatedMetrics(newMetrics);
      toast.success(`Reoptimized allocation for an extra $${Math.round(incBudget).toLocaleString()}`);
    } catch (error) {
      console.error('Error reoptimizing budget:', error);
      toast.error("We couldn't reoptimize the budget. Please try again.");
    } finally {
      setIsRecalculating({});
      setHasPendingEdits(false);
    }
  }, [incBudget, region, quarter, optimization]);

  // Keep every recommended budget as entered. Refresh forecast metrics only for channels that changed.
  // If an incremental budget is set, run a full re-optimization instead — it
  // supersedes any per-channel edits since it reallocates every channel at once.
  const handleCalculate = useCallback(() => {
    if (incBudget > 0) {
      handleReoptimize();
      return;
    }

    const currentBudgets = { ...budgetInputsRef.current };
    const committed = committedBudgetsRef.current;
    const nextCommitted = {};

    optimization.forEach((opt) => {
      const name = opt.name;
      const next = name in currentBudgets ? Number(currentBudgets[name]) || 0 : 0;
      const prev = Number(committed[name]) || 0;
      nextCommitted[name] = next;
      if (next !== prev) {
        recalculateMetrics(name, next);
      }
    });

    committedBudgetsRef.current = nextCommitted;
    setHasPendingEdits(false);
  }, [incBudget, handleReoptimize, optimization, recalculateMetrics]);

  const handleManualRefresh = () => {
    handleCalculate();
  };

  const handleBudgetChange = (channel, value) => {
    setHasPendingEdits(true);
    if (value === '' || value === null) {
      setBudgetInputs((prev) => {
        const newInputs = { ...prev };
        delete newInputs[channel];
        return newInputs;
      });
    } else {
      const numericValue = parseFloat(value);
      if (!isNaN(numericValue)) {
        setBudgetInputs((prev) => ({
          ...prev,
          [channel]: numericValue,
        }));
      }
    }
  };

  const handleIncBudgetBlur = () => {
    const cleaned = String(incBudgetText).replace(/[,$]/g, '');
    const parsed = cleaned === '' ? 0 : parseFloat(cleaned);
    const next = Number.isNaN(parsed) ? 0 : Math.max(0, parsed);
    if (next !== incBudget) setHasPendingEdits(true);
    setIncBudget(next);
    setIncBudgetText(Math.round(next).toLocaleString());
  };

  const anyRecalculating = Object.values(isRecalculating).some(Boolean);
  const canCalculate = hasPendingEdits && !anyRecalculating && isCurrentQuarter;
  const editingDisabled = anyRecalculating || !isCurrentQuarter;

  // Function to get color based on saturation percentage
  // 0-50%: shades of green, 50-70%: shades of yellow, 70-100%: shades of red
  const getSaturationColor = (saturation) => {
    const sat = Math.min(100, Math.max(0, saturation));
    let r, g, b;

    if (sat <= 50) {
      const t = sat / 50;
      r = Math.round(34 + (238 - 34) * t);
      g = Math.round(197 + (220 - 197) * t);
      b = Math.round(94 + (130 - 94) * t);
    } else if (sat <= 70) {
      const t = (sat - 50) / 20;
      r = Math.round(238 + (255 - 238) * t);
      g = Math.round(220 + (193 - 220) * t);
      b = Math.round(130 + (7 - 130) * t);
    } else {
      const t = (sat - 70) / 30;
      r = Math.round(255 - (255 - 180) * t);
      g = Math.round(150 - (150 - 0) * t);
      b = Math.round(150 - (150 - 0) * t);
    }

    return `rgb(${r}, ${g}, ${b})`;
  };

  const getBudgetMetrics = (opt) => {
    const channelName = opt.name;
    const userBudget = budgetInputs[channelName] || 0;
    const recalculated = recalculatedMetrics[channelName];

    const originalSaturation = opt.saturation_pct;
    const recalculatedSaturation = recalculated?.saturation_pct;
    let saturationValue = toNumber(
      recalculatedSaturation !== undefined ? recalculatedSaturation : originalSaturation
    );

    return {
      totalBudget: toNumber(opt.total_budget),
      totalSpend: toNumber(opt.total_spend),
      expectedPTsRemainingBudget: toNumber(opt.expected_PTs_remaining_budget),
      remainingBudget: toNumber(opt.remaining_budget),
      budgetRemainingPct: toNumber(opt.budget_remaining_pct),
      recommended: userBudget,
      expectedPTs: toNumber(recalculated?.expected_PTs ?? opt.expected_PTs_recommeneded),
      costPerPT: toNumber(recalculated?.cost_per_PT ?? opt.cost_per_PT),
      saturationPct: saturationValue,
      headroom: toNumber(recalculated?.headroom ?? opt.headroom),
    };
  };

  return (
    <div className="mt-6">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <label className="text-sm font-semibold text-mongodb-gray-dark3 whitespace-nowrap">
          Incremental budget
        </label>
        <div className="flex items-center gap-1">
          <span className="text-sm text-gray-700">$</span>
          <input
            type="text"
            value={incBudgetText}
            disabled={editingDisabled}
            onChange={(e) => {
              const raw = e.target.value.replace(/[,$]/g, '');
              if (raw === '' || /^\d*\.?\d*$/.test(raw)) {
                setIncBudgetText(e.target.value.replace(/[^\d.,]/g, ''));
              }
            }}
            onFocus={() => setIncBudgetText(String(Math.round(incBudget || 0)))}
            onBlur={handleIncBudgetBlur}
            className="w-28 px-2 py-1.5 border border-gray-300 rounded text-sm text-center disabled:bg-gray-100 disabled:text-gray-400"
            placeholder="0"
          />
        </div>
        <span className="text-xs text-gray-500">
          {isCurrentQuarter
            ? 'Set an incremental budget and Calculate to reallocate it optimally across every channel. Editing a single Recommended Budget cell instead leaves every other channel as-is.'
            : 'Editing is only available for the current, still-open quarter — this quarter has already ended.'}
        </span>
        <button
          type="button"
          onClick={handleCalculate}
          disabled={!canCalculate}
          title={!isCurrentQuarter ? 'Editing is only available for the current, still-open quarter' : undefined}
          className={`ml-auto text-sm font-semibold px-4 py-1.5 rounded border transition-colors ${
            canCalculate
              ? 'border-mongodb-green-dark2 text-mongodb-green-dark2 bg-white hover:bg-mongodb-green-dark2 hover:bg-opacity-10 cursor-pointer'
              : 'border-gray-300 text-gray-400 bg-gray-50 cursor-not-allowed'
          }`}
        >
          {anyRecalculating ? 'Calculating…' : 'Calculate'}
        </button>
      </div>

      <table className="w-full border-collapse border border-gray-200 rounded-2xl overflow-hidden text-sm">
      <thead>
        <tr className="bg-gray-100">
            <th className="border border-gray-200 px-2 py-2 text-left font-semibold text-mongodb-gray-dark3 text-sm w-[90px]">Channel</th>
            <th className="border border-gray-200 px-2 py-2 text-center font-semibold text-mongodb-gray-dark3 text-sm w-[110px]">Total Budget</th>
            <th className="border border-gray-200 px-2 py-2 text-center font-semibold text-mongodb-gray-dark3 text-sm w-[110px]">Total Spend</th>
            <th className="border border-gray-200 px-2 py-2 text-center font-semibold text-mongodb-gray-dark3 text-sm w-[120px]">Remaining Budget</th>
            <th className="border border-gray-200 px-2 py-2 text-center font-semibold text-mongodb-gray-dark3 text-sm w-[100px] whitespace-normal leading-tight">
              <div className="flex flex-col">
                <span className="text-xs">Forecasted PT's</span>
                <span className="text-xs">Remaining Budget</span>
              </div>
            </th>
            <th className="border border-gray-200 px-2 py-2 text-center font-semibold text-mongodb-gray-dark3 text-sm w-[100px]">% Budget Remaining</th>
            <th className="border border-gray-200 px-2 py-2 text-center font-semibold text-mongodb-gray-dark3 text-sm w-[110px] whitespace-normal leading-tight">
              <div className="flex items-center justify-center gap-1 flex-col">
                <span className="text-xs">Recommended</span>
                <div className="flex items-center gap-1">
                  <span className="text-xs">Budget</span>
                  <button
                    onClick={handleManualRefresh}
                    disabled={editingDisabled}
                    className="hover:bg-gray-200 rounded p-0.5 transition-colors disabled:opacity-50"
                    title="Refresh metrics for edited channels"
                  >
                    <RefreshCw className="w-3 h-3 text-mongodb-green-dark2" />
                  </button>
                </div>
              </div>
            </th>
            <th className="border border-gray-200 px-2 py-2 text-center font-semibold text-mongodb-gray-dark3 text-sm w-[100px] whitespace-normal leading-tight">
              <div className="flex flex-col">
                <span className="text-xs">Forecasted PTs</span>
                <span className="text-xs">Recommended</span>
              </div>
            </th>
            <th className="border border-gray-200 px-2 py-2 text-center font-semibold text-mongodb-gray-dark3 text-sm w-[85px]">Cost/PT</th>
            <th className="border border-gray-200 px-2 py-2 text-center font-semibold text-mongodb-gray-dark3 text-sm w-[95px]">% Saturation</th>
            <th className="border border-gray-200 px-2 py-2 text-center font-semibold text-mongodb-gray-dark3 text-sm w-[95px]">Headroom</th>
        </tr>
      </thead>
      <tbody>
          {optimization.map((opt, index) => {
            const metrics = getBudgetMetrics(opt);
            const channelName = opt.name;
            const isRecalculatingChannel = isRecalculating[channelName];

            return (
          <tr key={index} className={`hover:bg-mongodb-gray-light3 ${index % 2 === 0 ? 'bg-white' : 'bg-mongodb-gray-light3'}`}>
                <td className="border border-gray-200 px-2 py-2 font-medium text-mongodb-gray-dark3 text-sm">{opt.name}</td>
                <td className="border border-gray-200 px-2 py-2 text-center text-gray-700 text-sm">${Math.ceil(metrics.totalBudget).toLocaleString()}</td>
                <td className="border border-gray-200 px-2 py-2 text-center text-gray-700 text-sm">${Math.round(metrics.totalSpend).toLocaleString()}</td>
                <td className="border border-gray-200 px-2 py-2 text-center text-gray-700 text-sm">${Math.ceil(metrics.remainingBudget).toLocaleString()}</td>
                <td className="border border-gray-200 px-2 py-2 text-center text-gray-700 text-sm">{metrics.expectedPTsRemainingBudget.toLocaleString()}</td>
                <td className="border border-gray-200 px-2 py-2 text-center text-gray-700 text-sm">{metrics.budgetRemainingPct.toFixed(1)}%</td>
                <td className="border border-gray-200 px-2 py-2 text-center">
                  <div className="flex items-center justify-center gap-1">
                    <span className="text-gray-700 text-sm">$</span>
                    <input
                      type="text"
                      disabled={editingDisabled}
                      value={
                        channelName in budgetInputs
                          ? Math.ceil(budgetInputs[channelName]).toLocaleString()
                          : (metrics.recommended > 0 ? Math.ceil(metrics.recommended).toLocaleString() : '')
                      }
                      onChange={(e) => {
                        const numericValue = e.target.value.replace(/[,$]/g, '');
                        if (numericValue === '' || !isNaN(numericValue)) {
                          const value = numericValue === '' ? '' : parseFloat(numericValue);
                          handleBudgetChange(channelName, value);
                        }
                      }}
                      onBlur={(e) => {
                        const numericValue = e.target.value.replace(/[,$]/g, '');
                        if (numericValue !== '') {
                          const num = parseFloat(numericValue);
                          if (!isNaN(num)) {
                            handleBudgetChange(channelName, num);
                          }
                        }
                      }}
                      className="w-24 px-1.5 py-1 border border-gray-300 rounded text-sm text-center"
                      placeholder="0"
                    />
                    {isRecalculatingChannel && (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-mongodb-green-dark2"></div>
                    )}
                  </div>
                </td>
                <td className="border border-gray-200 px-2 py-2 text-center text-gray-700 text-sm">
                  {isRecalculatingChannel ? '...' : metrics.expectedPTs.toLocaleString()}
                </td>
                <td className="border border-gray-200 px-2 py-2 text-center text-gray-700 text-sm">
                  {isRecalculatingChannel ? '...' : `$${Math.round(metrics.costPerPT).toLocaleString()}`}
                </td>
                <td
                  className="border border-gray-200 px-2 py-2 text-center font-medium text-sm"
                  style={{
                    backgroundColor: getSaturationColor(metrics.saturationPct),
                    color: metrics.saturationPct > 70 ? 'white' : 'black',
                  }}
                >
                  {isRecalculatingChannel ? '...' : `${metrics.saturationPct.toFixed(1)}%`}
                </td>
                <td className="border border-gray-200 px-2 py-2 text-center text-gray-700 text-sm">
                  {isRecalculatingChannel ? '...' : `$${metrics.headroom.toLocaleString()}`}
            </td>
          </tr>
            );
          })}
          {/* Total Row */}
          {(() => {
            const totalMetrics = optimization.reduce((acc, opt) => {
              const metrics = getBudgetMetrics(opt);

              acc.totalBudget += metrics.totalBudget;
              acc.totalSpend += metrics.totalSpend;
              acc.expectedPTsRemainingBudget += metrics.expectedPTsRemainingBudget;
              acc.remainingBudget += metrics.remainingBudget;
              acc.recommendedBudget += metrics.recommended;
              acc.expectedPTs += metrics.expectedPTs;
              acc.headroom += metrics.headroom;

              return acc;
            }, {
              totalBudget: 0,
              totalSpend: 0,
              expectedPTsRemainingBudget: 0,
              remainingBudget: 0,
              recommendedBudget: 0,
              expectedPTs: 0,
              headroom: 0,
            });

            return (
              <tr className="bg-gray-100 font-bold">
                <td className="border border-gray-200 px-2 py-2 font-semibold text-mongodb-gray-dark3 text-sm">Total</td>
                <td className="border border-gray-200 px-2 py-2 text-center text-gray-700 text-sm">${Math.ceil(totalMetrics.totalBudget).toLocaleString()}</td>
                <td className="border border-gray-200 px-2 py-2 text-center text-gray-700 text-sm">${Math.round(totalMetrics.totalSpend).toLocaleString()}</td>
                <td className="border border-gray-200 px-2 py-2 text-center text-gray-700 text-sm">${Math.ceil(totalMetrics.remainingBudget).toLocaleString()}</td>
                <td className="border border-gray-200 px-2 py-2 text-center text-gray-700 text-sm">{totalMetrics.expectedPTsRemainingBudget.toLocaleString()}</td>
                <td className="border border-gray-200 px-2 py-2 text-center text-gray-700 text-sm">{((totalMetrics.remainingBudget / (totalMetrics.totalBudget || 1)) * 100).toFixed(1)}%</td>
                <td className="border border-gray-200 px-2 py-2 text-center text-gray-700 text-sm">${Math.round(totalMetrics.recommendedBudget).toLocaleString()}</td>
                <td className="border border-gray-200 px-2 py-2 text-center text-gray-700 text-sm">{totalMetrics.expectedPTs.toLocaleString()}</td>
                <td className="border border-gray-200 px-2 py-2 text-center text-gray-700 text-sm">
                  {totalMetrics.expectedPTs > 0
                    ? `$${Math.round(totalMetrics.recommendedBudget / totalMetrics.expectedPTs).toLocaleString()}`
                    : '-'}
                </td>
                <td className="border border-gray-200 px-2 py-2 text-center text-gray-700 text-sm">-</td>
                <td className="border border-gray-200 px-2 py-2 text-center text-gray-700 text-sm">${totalMetrics.headroom.toLocaleString()}</td>
              </tr>
            );
          })()}
      </tbody>
    </table>
  </div>
 );
};

// const InsightBox = ({ title, children }) => (
//   <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded">
//     <h4 className="text-green-800 font-semibold mb-2">{title}</h4>
//     {children}
//   </div>
// );

const PhaseCard = ({ phase, period, actions, lift }) => (
  <div className="bg-white border-l-4 border-mongodb-green-dark2 p-4 rounded-2xl shadow-md">
    <h4 className="text-xl font-bold text-mongodb-gray-dark3 mb-2">{phase}: {period}</h4>
    <ul className="text-gray-600 text-sm space-y-1">
      {actions.map((action, index) => (
        <li key={index}>• {action}</li>
      ))}
      <li className="font-semibold">• Expected lift: {lift}</li>
    </ul>
  </div>
);

// Add this function near the top of the file
// UNUSED: Only renderPiePercentLabel is used in the component
// const renderPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, value }) => {
//   const RADIAN = Math.PI / 180;
//   const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
//   const x = cx + radius * Math.cos(-midAngle * RADIAN);
//   const y = cy + radius * Math.sin(-midAngle * RADIAN);
// 
//   return (
//     <text
//       x={x}
//       y={y}
//       fill="#3D4F58"
//       textAnchor="middle"
//       dominantBaseline="central"
//       fontSize={16}
//       fontWeight="bold"
//     >
//       {value}
//     </text>
//   );
// };

const renderPiePercentLabel = ({ percent, cx, cy, midAngle, innerRadius, outerRadius, name }) => {
  const RADIAN = Math.PI / 180;
  
  // Threshold removed - showing all labels to display Reddit_selfserve
  // Previous threshold was: if (percent < 0.01) return null;
  
  // Position label outside the pie for better readability
  const radius = outerRadius + 30;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  
  // Determine text anchor based on position
  const textAnchor = x > cx ? 'start' : x < cx ? 'end' : 'middle';

  return (
    <text
      x={x}
      y={y}
      fill="#3D4F58"
      textAnchor={textAnchor}
      dominantBaseline="central"
      fontSize={14}
      fontWeight="600"
    >
      {(percent * 100).toFixed(1)}%
    </text>
  );
};

const MMMMPresentation = () => {
  const [chartData, setChartData] = useState([]);
  const [comprehensiveData, setComprehensiveData] = useState([]);
  const [periods, setPeriods] = useState({});
  const [correlationPeriod, setCorrelationPeriod] = useState({ start: null, end: null });
  const [kpiData, setKpiData] = useState({
    train_mape: '0%',
    validation_mape: '0%',
    total_budget: '$0M'
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isOptimizationLoading, setIsOptimizationLoading] = useState(true);
  const [error, setError] = useState(null);
  const [infoPopup, setInfoPopup] = useState({ isOpen: false, title: '', content: '' });
  const [isExampleModalOpen, setIsExampleModalOpen] = useState(false);
  const [channelAttribution, setChannelAttribution] = useState([]);
  const [estimatedPtsFullyMatured, setEstimatedPtsFullyMatured] = useState({});
  const [optimization, setOptimization] = useState([]);
  const [correlationData, setCorrelationData] = useState([]);
  const [optimizationSummary, setOptimizationSummary] = useState({});
  const [recalculatedMetricsFromTable, setRecalculatedMetricsFromTable] = useState({});
  const [budgetInputsFromTable, setBudgetInputsFromTable] = useState({});
  const [selectedRegion, setSelectedRegion] = useState('GLOBAL');
  const [currentOptimizationRegion, setCurrentOptimizationRegion] = useState('GLOBAL');
  const [selectedQuarter, setSelectedQuarter] = useState('Q4FY26');  // Changed from 'Q4' to 'Q4FY26'
  const [availableQuarters, setAvailableQuarters] = useState([]);  // For dynamic quarter detection
  const [quartersLoaded, setQuartersLoaded] = useState(false);  // Track if quarters are loaded to prevent race condition
  
  const { setContext: setAskAnalystContext } = useAskAnalyst();

  useEffect(() => {
    setAskAnalystContext({
      region: selectedRegion,
      quarter: selectedQuarter,
      activeTab: 'dashboard',
    });
  }, [selectedRegion, selectedQuarter, setAskAnalystContext]);
  
  // Locked 9-channel taxonomy
  const CHANNEL_DISPLAY_ORDER = [
    'Google Brand',
    'Google Non-Brand',
    'Bing Brand',
    'Bing Non-Brand',
    'LinkedIn',
    'Meta',
    'Display',
    'YouTube',
    'All Channels',
  ];

  // Sorting function to apply custom order
  const sortByChannelOrder = (data, nameKey = 'name') => {
    return [...data].sort((a, b) => {
      const indexA = CHANNEL_DISPLAY_ORDER.indexOf(a[nameKey]);
      const indexB = CHANNEL_DISPLAY_ORDER.indexOf(b[nameKey]);
      
      // If channel not in order array, put at end
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      
      return indexA - indexB;
    });
  };
  
  // Helper function to format quarter string for display
  // Example: "Q4FY26" -> "Q4 FY26"
  const formatQuarterDisplay = (quarter) => {
    if (!quarter) return '';
    // Insert space before FY: Q4FY26 -> Q4 FY26
    return quarter.replace(/FY/, ' FY');
  };
  
  // LightweightMMM visualization data
  const [contributionData, setContributionData] = useState([]);
  
  // const macroData = useMemo(() => generateMacroData(), []);
  // const seasonalityData = useMemo(() => generateSeasonalityData(), []);
  const correlationColumns = Array.from(
    new Set([
      ...correlationData.map(c => c.x),
      ...correlationData.map(c => c.y)
    ])
  );

  // Measure the correlation matrix container so cells can scale with the card width
  const correlationWrapRef = useRef(null);
  const [correlationWrapWidth, setCorrelationWrapWidth] = useState(0);

  useEffect(() => {
    const el = correlationWrapRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;

    setCorrelationWrapWidth(el.getBoundingClientRect().width);

    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect?.width;
      if (width) setCorrelationWrapWidth(width);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  
  // Map backend column names to user-friendly display names
  // Supports both Prophet model names and MMM/CSV names
  const columnDisplayNames = {
    // Prophet model column names (from prophet_global_model.joblib)
    'Bing': 'Bing',
    'Facebook': 'Facebook',
    'Linkedin': 'Linkedin',
    'GDN': 'GDN',
    'Google': 'Google',
    // MMM/CSV column names (legacy)
    paid_search_spend: 'Paid Search',
    meta_spend: 'Facebook Ads',
    linkedin_spend: 'LinkedIn',
    display_spend: 'Display',
    email_opens: 'Email Opens',
    organic_traffic: 'Organic Traffic',
    // Economic/External variables
    unemployment_rate: 'Unemployment Rate',
    customer_purchase_index: 'Customer Purchase Index',
    companyPageEngagements: 'LinkedIn Organic Engagements',
    companyPageViews: 'LinkedIn Organic Views',
    companyPageFollows: 'LinkedIn Organic Follows',
    is_holiday: 'Is Holiday',
    // Common
    revenue: 'PT_Count'
  };
  
  // Open Info popup with business narrative
  const openInfoPopup = useCallback((narrativeKey, title) => {
    setInfoPopup({ isOpen: true, title, content: vizNarratives[narrativeKey] });
  }, []);

  // Close Info popup
  const closeInfoPopup = useCallback(() => {
    setInfoPopup({ isOpen: false, title: '', content: '' });
  }, []);

  // Correlation matrix color mapping using MongoDB color palette with LIGHT colors
  const getCorrelationColor = (value) => {
    // MongoDB official brand colors - Using LIGHTER shades for better readability
    const mongodbGreenLight3 = '#E3FCF7'; // Very light green for weak positive
    const mongodbGreenLight2 = '#C0FAE6'; // Light green for medium positive  
    const mongodbGreenBase = '#00ED64';   // Bright green for strong positive
    const mongodbWhite = '#FFFFFF';       // White for neutral
    const mongodbRedLight3 = '#FFEAE5';   // Very light red for weak negative
    const mongodbRedLight2 = '#FFCDC7';   // Light red for medium negative
    const mongodbRedBase = '#DB3030';     // Red for strong negative
    
    // Convert hex colors to RGB
    const hexToRgb = (hex) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
      } : null;
    };
    
    // RGB interpolation function
    const interpolateColor = (color1, color2, t) => {
      const rgb1 = hexToRgb(color1);
      const rgb2 = hexToRgb(color2);
      const r = Math.round(rgb1.r + (rgb2.r - rgb1.r) * t);
      const g = Math.round(rgb1.g + (rgb2.g - rgb1.g) * t);
      const b = Math.round(rgb1.b + (rgb2.b - rgb1.b) * t);
      return `rgb(${r},${g},${b})`;
    };
    
    if (value < 0) {
      // For negative correlations: gradual progression through light shades
      const absValue = Math.abs(value);
      if (absValue <= 0.3) {
        // Very weak negative (0 to -0.3): white to very light red
        const t = absValue / 0.3;
        return interpolateColor(mongodbWhite, mongodbRedLight3, t);
      } else if (absValue <= 0.6) {
        // Weak to medium negative (-0.3 to -0.6): light red to medium red
        const t = (absValue - 0.3) / 0.3;
        return interpolateColor(mongodbRedLight3, mongodbRedLight2, t);
      } else {
        // Strong negative (-0.6 to -1.0): medium red to base red
        const t = (absValue - 0.6) / 0.4;
        return interpolateColor(mongodbRedLight2, mongodbRedBase, t);
      }
    } else {
      // For positive correlations: gradual progression through light shades
      if (value <= 0.3) {
        // Very weak positive (0 to 0.3): white to very light green
        const t = value / 0.3;
        return interpolateColor(mongodbWhite, mongodbGreenLight3, t);
      } else if (value <= 0.6) {
        // Weak to medium positive (0.3 to 0.6): light green to medium green
        const t = (value - 0.3) / 0.3;
        return interpolateColor(mongodbGreenLight3, mongodbGreenLight2, t);
      } else {
        // Strong positive (0.6 to 1.0): medium green to base green
        const t = (value - 0.6) / 0.4;
        return interpolateColor(mongodbGreenLight2, mongodbGreenBase, t);
      }
    }
  };
  
  // Fetch available quarters when component mounts (runs once)
  useEffect(() => {
    console.log('\n🔍 QUARTER DETECTION - Fetching available quarters from backend...');
    
    fetch(`${API_URL}/api/available-quarters`, {
      headers: getAuthHeaders()
    })
      .then(res => {
        console.log('  API Response status:', res.status);
        if (!res.ok) {
          throw new Error(`Failed to fetch quarters: ${res.status} ${res.statusText}`);
        }
        return res.json();
      })
      .then(data => {
        console.log('✅ Available quarters received from backend:');
        console.log('  Quarters:', data.quarters);
        console.log('  Default:', data.default);
        
        setAvailableQuarters(data.quarters);
        
        // Set default to newest quarter (first in array)
        if (data.quarters.length > 0) {
          setSelectedQuarter(data.default);
          console.log(`✓ Selected quarter set to: ${data.default} (newest)`);
        } else {
          console.warn('⚠️ No quarters returned from API');
        }
        
        // Signal that quarters are loaded - this will trigger data fetch
        setQuartersLoaded(true);
        console.log('✓ Quarters loaded flag set to true - data fetch will now proceed');
      })
      .catch(error => {
        console.error('❌ ERROR fetching quarters:', error);
        // Fallback to hardcoded values if API fails
        const fallbackQuarters = ['Q4FY26', 'Q3FY26'];
        console.log('⚠️ Using fallback quarters:', fallbackQuarters);
        setAvailableQuarters(fallbackQuarters);
        setSelectedQuarter('Q4FY26');
        
        // Even on error, set quartersLoaded to true so app doesn't hang
        setQuartersLoaded(true);
        console.log('✓ Quarters loaded flag set to true (fallback mode)');
      });
  }, []); // Empty dependency array = run once on component mount
  
  useEffect(() => {
    // RACE CONDITION FIX: Don't fetch data until quarters are loaded
    if (!quartersLoaded) {
      console.log('⏸️ Data fetch skipped - waiting for quarters to load first');
      return;
    }
    
    console.log('▶️ Quarters loaded - proceeding with data fetch');
    setIsLoading(true);
    setIsOptimizationLoading(true); // Start optimization loading
    setOptimization([]); // Clear previous optimization data
    setChannelAttribution([]); // Clear previous channel attribution data to prevent stale data display
    setEstimatedPtsFullyMatured({});
    setCurrentOptimizationRegion(null); // Mark current data as stale
    
    // Only add region parameter if a specific region is selected
    const regionParam = selectedRegion !== 'GLOBAL' ? `region=${selectedRegion}` : '';
    const quarterParam = `quarter=${selectedQuarter}`;
    const queryParams = [regionParam, quarterParam].filter(p => p).join('&');
    const queryString = queryParams ? `?${queryParams}` : '';
    
    // Debug logging - Enhanced for quarter troubleshooting
    console.log('🔍 QUARTER DEBUG - Frontend Fetch:');
    console.log('  Selected Region:', selectedRegion);
    console.log('  Selected Quarter:', selectedQuarter);
    console.log('  Quarter Parameter:', quarterParam);
    console.log('  Full Query String:', queryString);
    console.log('  Full API URL:', `${API_URL}/api/comprehensive-forecast${queryString}`);
    console.log('Previous optimization region:', currentOptimizationRegion);
    console.log('Cleared channel attribution and optimization data for fresh fetch');
    
    // Fetch only comprehensive data (Prophet model) - no need for ARIMAX
    fetch(`${API_URL}/api/comprehensive-forecast${queryString}`, {
      headers: getAuthHeaders()
    })
      .then((comprehensiveRes) => {
        if (!comprehensiveRes.ok) {
          throw new Error('Network response was not ok');
        }
        return comprehensiveRes.json();
      })
      .then((comprehensiveData) => {
        console.log('Comprehensive data received:', comprehensiveData);
        console.log('First few entries:', comprehensiveData.comprehensive_data?.slice(0, 3));
        console.log('Last few entries:', comprehensiveData.comprehensive_data?.slice(-3));
        
        // DEBUG: Check correlation matrix in response
        console.log('🔍 DEBUG FETCH: Correlation matrix in API response:');
        console.log('  Has correlation_matrix key?', 'correlation_matrix' in comprehensiveData);
        console.log('  correlation_matrix value:', comprehensiveData.correlation_matrix);
        console.log('  Is array?', Array.isArray(comprehensiveData.correlation_matrix));
        console.log('  Length:', (comprehensiveData.correlation_matrix || []).length);
        
        // Check if confidence intervals exist
        const hasConfidenceIntervals = comprehensiveData.comprehensive_data?.some(entry => 
          entry.confidence_lower !== null && entry.confidence_upper !== null
        );
        console.log('Has confidence intervals:', hasConfidenceIntervals);
        
        // No need for separate forecast data transformation since we're using comprehensive data

        // Transform comprehensive data for train/validation/forecast visualization
        const comprehensiveTransformed = comprehensiveData.comprehensive_data.map((entry, index) => ({
          week: entry.week,
          date: entry.date,
          actual: entry.actual,
          predicted: entry.predicted,
          period: entry.period,
          confidence_range: [
            entry.confidence_lower || 0,  // lower bound
            entry.confidence_upper || entry.predicted || 0  // upper bound
          ],
          confidence_lower: entry.confidence_lower || 0,
          confidence_upper: entry.confidence_upper || entry.predicted || 0
        }));
        
        // Filter to show only last 104 weeks (2 years)
        const filteredData = comprehensiveTransformed.slice(-104);
        
        setComprehensiveData(filteredData);
        
        // Recalculate periods from filtered data
        const historicalWeeks = filteredData.filter(d => d.period === 'historical');
        const forecastWeeks = filteredData.filter(d => d.period === 'forecast');

        const recalculatedPeriods = {
          historical_start: historicalWeeks.length > 0 ? historicalWeeks[0].date : null,
          historical_end: historicalWeeks.length > 0 ? historicalWeeks[historicalWeeks.length - 1].date : null,
          forecast_start: forecastWeeks.length > 0 ? forecastWeeks[0].date : null,
          forecast_end: forecastWeeks.length > 0 ? forecastWeeks[forecastWeeks.length - 1].date : null,
        };

        setPeriods(recalculatedPeriods);

        // Set correlation period if available
        if (comprehensiveData.periods?.correlation_start && comprehensiveData.periods?.correlation_end) {
          setCorrelationPeriod({
            start: comprehensiveData.periods.correlation_start,
            end: comprehensiveData.periods.correlation_end
          });
        }

        // Update KPI data - use comprehensive data (Prophet model) instead of forecast data (ARIMAX)
        setKpiData({
          train_mape: `${comprehensiveData.kpis.train_mape}%`,
          validation_mape: `${comprehensiveData.kpis.validation_mape}%`,
          total_budget: comprehensiveData.kpis.total_budget,
          total_pt_forecast: comprehensiveData.kpis.total_pt_forecast,
          actual_pt_forecast: comprehensiveData.kpis.actual_pt_forecast,
        });
        
        // Set channel attribution immediately (calculated first in backend, independent of optimization)
        if (comprehensiveData.channel_attribution && comprehensiveData.channel_attribution.length > 0) {
          console.log('✓ Channel attribution data received for region:', selectedRegion);
          setChannelAttribution(comprehensiveData.channel_attribution);
        } else {
          console.warn('⚠️ No channel attribution data received');
          setChannelAttribution([]);
        }
        setEstimatedPtsFullyMatured(comprehensiveData.estimated_pts_fully_matured || {});
        
        // Debug optimization data
        console.log('Optimization data received:', comprehensiveData.optimization);
        console.log('Optimization array length:', (comprehensiveData.optimization || []).length);
        console.log('Data is for region:', selectedRegion);
        console.log('Current displayed region:', currentOptimizationRegion);
        
        // DEBUG: Correlation Matrix Data
        console.log('🔍 DEBUG CORRELATION MATRIX:');
        console.log('  Raw correlation_matrix from backend:', comprehensiveData.correlation_matrix);
        console.log('  Is array?', Array.isArray(comprehensiveData.correlation_matrix));
        console.log('  Length:', (comprehensiveData.correlation_matrix || []).length);
        if (comprehensiveData.correlation_matrix && comprehensiveData.correlation_matrix.length > 0) {
          console.log('  First 3 items:', comprehensiveData.correlation_matrix.slice(0, 3));
          console.log('  Sample item structure:', JSON.stringify(comprehensiveData.correlation_matrix[0], null, 2));
        }
        
        setOptimization(comprehensiveData.optimization || []);
        setCorrelationData(comprehensiveData.correlation_matrix || []);
        setOptimizationSummary(comprehensiveData.optimization_summary || {});
        
        // DEBUG: After setting state
        console.log('  ✓ setCorrelationData called with:', (comprehensiveData.correlation_matrix || []).length, 'items');
        
        // Debug: Check saturation_pct in initial optimization data
        if (comprehensiveData.optimization && comprehensiveData.optimization.length > 0) {
          console.log('🔍 DEBUG: Initial optimization data saturation_pct values:');
          comprehensiveData.optimization.forEach(opt => {
            console.log(`  - ${opt.name}: saturation_pct = ${opt.saturation_pct} (type: ${typeof opt.saturation_pct})`);
          });
        }
        
        // Mark optimization as loaded only if we have data
        if (comprehensiveData.optimization && comprehensiveData.optimization.length > 0) {
          console.log('✓ Optimization data loaded successfully for region:', selectedRegion);
          setCurrentOptimizationRegion(selectedRegion); // Track which region this data is for
          setIsOptimizationLoading(false);
        } else {
          console.warn('⚠️ No optimization data received, keeping loading state');
          // Still set to false to avoid infinite loading, but log the issue
          setIsOptimizationLoading(false);
        }
      })
      .catch((error) => {
        console.error('Error fetching data:', error);
        setError('Failed to load data. Please try again later.');
        setIsOptimizationLoading(false); // Stop loading on error too
        setChannelAttribution([]); // Clear attribution on error too
        setEstimatedPtsFullyMatured({});
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [selectedRegion, selectedQuarter, quartersLoaded]);

  // Dynamically generate cost per PT data from channelAttribution (SORTED by cost_per_pt ascending)
  const costPerPtData = channelAttribution
    .map(ch => ({
      name: ch.name,
      cost_per_pt: ch.cost_per_pt || 0
    }))
    .sort((a, b) => a.cost_per_pt - b.cost_per_pt); // Sort ascending by cost_per_pt

  // Dynamically generate pie chart data from optimization (SORTED)
  // Handle both number and string formats (CSV has numbers, some APIs might send formatted strings)
  const currentBudgetData = sortByChannelOrder(optimization.map(opt => {
    let value;
    if (typeof opt.current === 'number') {
      value = opt.current; // Already a number in dollars
    } else {
      const valueStr = opt.current.replace(/[^0-9.]/g, ""); // Extract number from string
      value = parseFloat(valueStr) * 1e3; // Convert thousands to dollars
    }
    return {
      name: opt.name,
      value: value
    };
  }));
  const optimalBudgetData = sortByChannelOrder(optimization.map(opt => {
    let value;
    if (typeof opt.recommended === 'number') {
      value = opt.recommended; // Already a number in dollars
    } else {
      const valueStr = opt.recommended.replace(/[^0-9.]/g, ""); // Extract number from string
      value = parseFloat(valueStr) * 1e3; // Convert thousands to dollars
    }
    return {
      name: opt.name,
      value: value
    };
  }));

  // Fetch LightweightMMM contribution visualization data
  useEffect(() => {
    const fetchMMMData = async () => {
      try {
        // Build region parameter
        const regionParam = selectedRegion !== 'GLOBAL' ? `?region=${encodeURIComponent(selectedRegion)}` : '';
        
        // Fetch contribution data
        const contributionRes = await fetch(`${API_URL}/api/mmm-contribution${regionParam}`, { headers: getAuthHeaders() });
        const contributionJson = await contributionRes.json();

        if (contributionJson.contribution_data) {
          // Filter to show only last 24 months (2 years)
          const lastTwoYearsData = contributionJson.contribution_data.slice(-24);
          setContributionData(lastTwoYearsData);
        }

        console.log(`✓ Loaded LightweightMMM contribution data for region: ${selectedRegion}`);
      } catch (err) {
        console.error('Error loading MMM contribution data:', err);
      }
    };

    fetchMMMData();
  }, [selectedRegion]);

  return (
    <div className="min-h-screen">
      <div className="p-4">
        {/* Region Selection Dropdown */}
      <div className="mb-6">
        <div className="bg-white rounded-2xl shadow-md p-4 border border-mongodb-gray-light2">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-mongodb-gray-dark3">Region Selection</h2>
            <div className="flex items-center gap-4">
              <select
                value={selectedRegion}
                onChange={(e) => setSelectedRegion(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-md bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-mongodb-green-dark2 shadow-sm"
              >
                <option value="GLOBAL">Global (All Regions)</option>
                <option value="APAC">APAC</option>
                <option value="EMEA">EMEA</option>
                <option value="INDIA">India</option>
                <option value="LATAM">LATAM</option>
                <option value="North America">North America</option>
              </select>
              {selectedRegion !== 'GLOBAL' && (
                <button
                  onClick={() => setSelectedRegion('GLOBAL')}
                  className="px-4 py-2 text-sm text-mongodb-green-dark2 hover:text-mongodb-green-dark1"
                >
                  Reset to Global View
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Slide 1: Overview */}
      <Slide title="Model Overview" subtitle="Train/Validation/Forecast Analysis">
        <TimeframeBar comprehensiveData={comprehensiveData} periods={periods} />
        <Card>
          <CardHeader>
            <div className="flex items-center justify-end gap-2">
              <button
                onClick={() => openInfoPopup('modelOverview', 'Model Overview - Business Problem')}
                className="p-2 text-mongodb-green-dark2 hover:bg-mongodb-green-dark2 hover:bg-opacity-10 rounded-full transition-colors"
                title="Learn about the business problem this solves"
              >
                <Info className="w-5 h-5" />
              </button>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="h-96 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-mongodb-green-dark2"></div>
              </div>
            ) : error ? (
              <div className="h-96 flex items-center justify-center text-red-500">
                {error}
              </div>
            ) : (
              <>
                <div className="h-96 mb-6">
                  <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={comprehensiveData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis 
                        tickFormatter={(value) => value.toLocaleString('en-US')}
                      />
                      <Tooltip 
                        content={({ active, payload, label }) => {
                          if (active && payload && payload.length) {
                            const data = payload[0].payload;
                            return (
                              <div className="bg-white p-3 border border-gray-300 rounded shadow-lg">
                                <p className="font-semibold">{`${data.date} - ${data.period}`}</p>
                                {data.actual && <p style={{ color: '#00684A' }}>Actual PT Count: {data.actual.toLocaleString('en-US')}</p>}
                                {data.predicted && (
                                  <p style={{ 
                                    color: data.period === 'train' ? '#3D4F58' : 
                                           data.period === 'validation' ? '#DB3030' : 
                                           '#FFC010' 
                                  }}>
                                    Predicted PT Count: {data.predicted.toLocaleString('en-US')}
                                  </p>
                                )}
                                {data.confidence_range && (
                                  <>
                                    <p style={{ color: '#889397' }}>Lower CI: {data.confidence_range[0].toLocaleString('en-US')}</p>
                                    <p style={{ color: '#889397' }}>Upper CI: {data.confidence_range[1].toLocaleString('en-US')}</p>
                                  </>
                                )}
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                      {/* Vertical separation lines */}
                      {comprehensiveData.length > 0 && (() => {
                        const historicalData = comprehensiveData.filter(d => d.period === 'historical');
                        const forecastData = comprehensiveData.filter(d => d.period === 'forecast');
                        
                        const lines = [];
                        
                        // Line between historical and forecast (Orange)
                        if (historicalData.length > 0 && forecastData.length > 0) {
                          const historicalEndDate = historicalData[historicalData.length - 1].date;
                          lines.push(
                            <ReferenceLine 
                              key="historical-forecast" 
                              x={historicalEndDate} 
                              stroke="#FF8C00" 
                              strokeWidth={2} 
                              strokeDasharray="5 5"
                            />
                          );
                        }
                        
                        return lines;
                      })()}
                      {/* Actual PT's line (only for historical period) */}
                      <Line 
                        type="monotone" 
                        dataKey={(entry) => entry.period === 'historical' ? entry.actual : null}
                        stroke="#13AA52" 
                        strokeWidth={2} 
                        name="Actual PT Count"
                        dot={false}
                        connectNulls={false}
                      />
                      
                      {/* Predicted PT's line for historical period */}
                      <Line 
                        type="monotone" 
                        dataKey={(entry) => entry.period === 'historical' ? entry.predicted : null}
                        stroke="#001E2B" 
                        strokeWidth={2} 
                        name="Predicted PT Count"
                        dot={false}
                        connectNulls={false}
                      />
                      
                      {/* Predicted PT's line for forecast period */}
                      <Line 
                        type="monotone" 
                        dataKey={(entry) => entry.period === 'forecast' ? entry.predicted : null}
                        stroke="#FFC010" 
                        strokeWidth={2} 
                        name="Forecast PT Count"
                        dot={false}
                        connectNulls={false}
                      />
                      
                      {/* Confidence bands (shaded areas) - rendered last to appear last in legend */}
                      <Area
                        type="monotone"
                        dataKey="confidence_range"
                        fill="#889397"
                        fillOpacity={0.4}
                        stroke="none"
                        connectNulls={false}
                        name="Confidence Interval"
                      />
                      
                      <Legend />
                      
                      {/* Vertical lines to separate periods */}
                      {periods.validation_start && (
                        <ReferenceLine 
                          x={comprehensiveData.find(d => d.period === 'validation')?.week} 
                          stroke="#8B4513" 
                          strokeDasharray="5 5" 
                          strokeWidth={2}
                          label={{ value: "Validation Start", position: "top" }}
                        />
                      )}
                      {periods.forecast_start && (
                        <ReferenceLine 
                          x={comprehensiveData.find(d => d.period === 'forecast')?.week} 
                          stroke="#DC2626" 
                          strokeDasharray="5 5" 
                          strokeWidth={2}
                          label={{ value: "Forecast Start", position: "top" }}
                        />
                      )}
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <MetricCard value={kpiData.train_mape} label="Train MAPE (%)" />
                  <MetricCard value={kpiData.validation_mape} label="Validation MAPE (%)" />
                  <MetricCard value={kpiData.total_pt_forecast ? kpiData.total_pt_forecast.toLocaleString() : "0"} label="Total PT's Forecasted" />
                  <MetricCard value={kpiData.actual_pt_forecast ? kpiData.actual_pt_forecast.toLocaleString() : "0"} label="Actual PT's (Forecast Period)" />
                </div>
                <p className="mt-3 text-xs text-mongodb-gray-dark2 italic">
                  MAPE measures average prediction error. Train MAPE = first 90% of pre-quarter weeks. Validation MAPE = last 10% chronological holdout. Chart lines use a model fit on all pre-quarter history.
                </p>
              </>
            )}
          </CardContent>
        </Card>
      </Slide>

      {/* Slide 2: Budget Optimization (moved up per Tom's feedback) */}
      <Slide title="Budget Optimization Recommendations" subtitle="Optimal allocation for maximum PTs">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-end gap-2">
              <button
                onClick={() => openInfoPopup('budgetOptimization', 'Budget Optimization - Business Problem')}
                className="p-2 text-mongodb-green-dark2 hover:bg-mongodb-green-dark2 hover:bg-opacity-10 rounded-full transition-colors"
                title="Learn about the business problem this solves"
              >
                <Info className="w-5 h-5" />
              </button>
            </div>
          </CardHeader>
          <CardContent>
            {isOptimizationLoading || optimization.length === 0 || currentOptimizationRegion !== selectedRegion ? (
              <div className="flex flex-col items-center justify-center py-20">
                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-mongodb-green-dark2 mb-4"></div>
                <p className="text-mongodb-gray-dark3 text-lg font-semibold">Running Budget Optimization for {selectedRegion}...</p>
                <p className="text-gray-500 text-sm mt-2">This may take a moment</p>
              </div>
            ) : (
              <>
                <div className="bg-mongodb-green-dark1 bg-opacity-10 border-2 border-mongodb-green-dark1 rounded-lg p-4 mb-6">
                  <h3 className="text-lg font-semibold mb-4 text-mongodb-gray-dark3">Budget Optimization Analysis</h3>
                  <OptimizationTable
                    key={`${selectedRegion}-${selectedQuarter}`}
                    optimization={sortByChannelOrder(optimization)}
                    region={selectedRegion}
                    quarter={selectedQuarter}
                    isCurrentQuarter={availableQuarters.length === 0 || selectedQuarter === availableQuarters[0]}
                    onMetricsUpdate={(metrics, budgets) => {
                      setRecalculatedMetricsFromTable(metrics);
                      setBudgetInputsFromTable(budgets);
                    }}
                  />
                </div>
              </>
            )}
            {(() => {
              // Helper function to get effective budget (user-modified or recommended)
              const getEffectiveBudget = (opt) => {
                const channelName = opt.name;
                // Use user-modified budget if available, else use recommended
                if (budgetInputsFromTable[channelName] && budgetInputsFromTable[channelName] > 0) {
                  return budgetInputsFromTable[channelName];
                }
                // Handle both number and string formats
                if (typeof opt.recommended === 'number') {
                  return opt.recommended; // Already a number in dollars
                } else {
                  const recommendedStr = opt.recommended.replace(/[^0-9.]/g, '');
                  return parseFloat(recommendedStr) * 1000; // Convert K to dollars
                }
              };

              // Calculate Incremental PTs (sum of all channel incremental PTs)
              // Use recalculated metrics if available, else use original
              const incrementalPTs = optimization.reduce((sum, opt) => {
                const channelName = opt.name;
                const recalculated = recalculatedMetricsFromTable[channelName];
                // Use recalculated expected_PTs if available, else use original
                const expectedPTs = recalculated?.expected_PTs || opt.expected_PTs_recommeneded || 0;
                return sum + expectedPTs;
              }, 0);

              // Calculate Total Budget (sum of all channel budgets)
              const totalBudget = optimization.reduce((sum, opt) => {
                return sum + getEffectiveBudget(opt);
              }, 0);

              // Calculate Cost/Incremental PT
              const costPerIncrementalPT = incrementalPTs > 0 
                ? totalBudget / incrementalPTs 
                : 0;

              return null;
            })()}
          </CardContent>
        </Card>
      </Slide>

      {/* Slide 3: Channel Attribution */}
      {(() => {
        // Static title without dates to avoid confusion
        const forecastPeriodTitle = "PT Contribution by Channel";
        
        return <Slide title={forecastPeriodTitle} subtitle="Breakdown of marketing impact by channel">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              {/* LEFT SIDE: Dynamic quarter toggle buttons */}
              <div className="inline-flex rounded-lg border border-mongodb-gray-light2 bg-white p-1">
                {availableQuarters.length > 0 ? (
                  availableQuarters.map((quarter) => (
                    <button
                      key={quarter}
                      onClick={() => {
                        console.log(`🔍 QUARTER DEBUG - ${quarter} Button Clicked`);
                        console.log(`  Previous quarter: ${selectedQuarter}`);
                        console.log(`  Setting quarter to: ${quarter}`);
                        setSelectedQuarter(quarter);
                      }}
                      className={`px-4 py-2 text-sm font-semibold rounded-md transition-colors cursor-pointer ${
                        selectedQuarter === quarter 
                          ? 'bg-mongodb-green-dark2 text-white' 
                          : 'text-mongodb-gray-dark3 hover:bg-mongodb-gray-light3'
                      }`}
                    >
                      {formatQuarterDisplay(quarter)}
                    </button>
                  ))
                ) : (
                  <div className="px-4 py-2 text-sm text-mongodb-gray-dark3">
                    Loading quarters...
                  </div>
                )}
              </div>
              
              {/* RIGHT SIDE: Existing info and AI buttons */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => openInfoPopup('channelAttribution', 'PT Contribution by Channel - Business Problem')}
                  className="p-2 text-mongodb-green-dark2 hover:bg-mongodb-green-dark2 hover:bg-opacity-10 rounded-full transition-colors"
                  title="Learn about the business problem this solves"
                >
                  <Info className="w-5 h-5" />
                </button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {channelAttribution.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20">
                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-mongodb-green-dark2 mb-4"></div>
                <p className="text-mongodb-gray-dark3 text-lg font-semibold">Loading Channel Attribution Data for {selectedRegion}...</p>
              </div>
            ) : (
              <>
            <div className="h-[32rem] mb-2">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={costPerPtData} margin={{ top: 20, right: 20, bottom: 30, left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="name" 
                    angle={-45}
                    textAnchor="end"
                    height={120}
                    tick={{ fontSize: 12, fontWeight: 'bold' }}
                  />
                  <YAxis 
                    domain={[0, 'auto']} 
                    tickFormatter={(value) => value.toLocaleString('en-US')}
                  />
                  <Tooltip 
                    formatter={(value) => Math.round(value).toLocaleString('en-US')}
                  />
                  <Bar dataKey="cost_per_pt" name="Cost per Actual PT">
                    {costPerPtData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={colors[index] ?? colors[index % colors.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="text-center mb-2">
              <p className="text-sm font-semibold text-mongodb-gray-dark3">Cost per Actual PT ($)</p>
            </div>
            <AttributionTable channelAttribution={sortByChannelOrder(channelAttribution)} estimatedPtsFullyMatured={estimatedPtsFullyMatured} />
              </>
            )}
          </CardContent>
        </Card>
      </Slide>
      })()}

      {/* Slide 4: Correlation Matrix (moved up; MMM breakdown now lives at the bottom) */}
      <Slide 
        title={`Correlation Matrix with PT Count${correlationPeriod.start && correlationPeriod.end ? ` (${correlationPeriod.start} to ${correlationPeriod.end})` : ''}`}
        subtitle="Marketing channels grouped first; macro indices grouped on the right"
      >
        <Card>
          <CardHeader>
            <div className="flex items-center justify-end gap-2">
              <button
                onClick={() => openInfoPopup('correlationMatrix', 'Correlation Matrix - Business Problem')}
                className="p-2 text-mongodb-green-dark2 hover:bg-mongodb-green-dark2 hover:bg-opacity-10 rounded-full transition-colors"
                title="Learn about the business problem this solves"
              >
                <Info className="w-5 h-5" />
              </button>
            </div>
          </CardHeader>
          <CardContent>
            {(() => {
              // Reworked: separate marketing channels from macro indices (Tom's feedback)
              const MACRO_VARS = new Set([
                'unemployment_rate',
                'customer_purchase_index',
                'is_holiday',
                'inflation',
                'cpi',
              ]);
              const isMacro = (v) => MACRO_VARS.has(v) || v === 'revenue' || v === 'PT_Count';
              const orderedColumns = [
                ...correlationColumns.filter((c) => !isMacro(c)),
                ...correlationColumns.filter((c) => isMacro(c)),
              ];
              const splitIdx = orderedColumns.findIndex((c) => isMacro(c));
              const dividerCol = splitIdx > 0 ? splitIdx : null;

              // Cells grow with the card width, floored so they stay readable
              // (the grid scrolls horizontally instead) and capped so they do
              // not balloon on ultrawide screens.
              const MIN_CELL_PX = 40;
              const MAX_CELL_PX = 80;
              const LABEL_RATIO = 1.8;
              const available = correlationWrapWidth || 0;
              const fitted = available > 0
                ? Math.floor(available / (orderedColumns.length + LABEL_RATIO))
                : 56;
              const cellPx = Math.min(MAX_CELL_PX, Math.max(MIN_CELL_PX, fitted));
              const labelPx = Math.round(cellPx * LABEL_RATIO);
              const cellSize = `${cellPx}px`;
              const gridTemplate = `${labelPx}px repeat(${orderedColumns.length}, ${cellPx}px)`;
              const matrixWidthPx = labelPx + orderedColumns.length * cellPx;
              const labelFontPx = Math.max(10, Math.min(13, Math.round(cellPx * 0.2)));
              const valueFontPx = Math.max(11, Math.min(15, Math.round(cellPx * 0.22)));
              const headerHeightPx = Math.max(56, cellPx);

              return (
                <div ref={correlationWrapRef} style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  {correlationData.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      <p>No correlation data available. Please check backend logs.</p>
                    </div>
                  )}

                  {correlationData.length > 0 && orderedColumns.length > 0 ? (
                    <>
                      <div style={{ width: '100%', overflowX: 'auto' }}>
                      {/* fit-content + auto margins keeps the grid centered when it
                          fits and still scrollable from the left edge when it does not */}
                      <div style={{ width: 'fit-content', margin: '0 auto' }}>
                        {/* Top labels */}
                        <div className="grid" style={{
                          gridTemplateColumns: gridTemplate
                        }}>
                          <div></div>
                          {orderedColumns.map((col, i) => (
                            <div
                              key={i}
                              className="font-semibold text-center text-mongodb-gray-dark3 flex items-end justify-center leading-tight px-0.5 pb-1"
                              style={{
                                width: cellSize,
                                minHeight: `${headerHeightPx}px`,
                                fontSize: `${labelFontPx}px`,
                                borderLeft: dividerCol !== null && i === dividerCol ? '2px solid #001E2B' : undefined,
                                paddingLeft: dividerCol !== null && i === dividerCol ? '4px' : undefined,
                              }}
                              title={isMacro(col) ? 'Macro indicator' : 'Marketing channel'}
                            >
                              {columnDisplayNames[col] || col.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            </div>
                          ))}
                        </div>
                        {/* Group header strip */}
                        {dividerCol !== null && (
                          <div className="grid" style={{ gridTemplateColumns: gridTemplate }}>
                            <div></div>
                            <div
                              className="uppercase tracking-wider text-mongodb-gray-dark2 text-center"
                              style={{ gridColumn: `2 / span ${dividerCol}`, fontSize: `${labelFontPx}px` }}
                            >
                              Marketing channels
                            </div>
                            <div
                              className="uppercase tracking-wider text-mongodb-gray-dark2 text-center"
                              style={{ gridColumn: `${dividerCol + 2} / span ${orderedColumns.length - dividerCol}`, fontSize: `${labelFontPx}px` }}
                            >
                              Macro indices
                            </div>
                          </div>
                        )}
                        {/* Matrix rows */}
                        {orderedColumns.map((rowVar, rowIdx) => (
                          <div key={rowVar} className="grid" style={{
                            gridTemplateColumns: gridTemplate,
                            borderTop: dividerCol !== null && rowIdx === dividerCol ? '2px solid #001E2B' : undefined,
                          }}>
                            <div
                              className="font-semibold flex items-center justify-end text-mongodb-gray-dark3 text-right leading-tight pr-2"
                              style={{ width: `${labelPx}px`, fontSize: `${labelFontPx}px` }}
                            >
                              {columnDisplayNames[rowVar] || rowVar.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            </div>
                            {orderedColumns.map((colVar, colIdx) => {
                              const cell = correlationData.find(c => c.x === rowVar && c.y === colVar);
                              return (
                                <div
                                  key={colVar}
                                  className={`flex items-center justify-center font-bold rounded border${rowVar === colVar ? ' font-extrabold' : ''}`}
                                  style={{
                                    width: cellSize,
                                    height: cellSize,
                                    fontSize: `${valueFontPx}px`,
                                    backgroundColor: getCorrelationColor(cell?.value ?? 0),
                                    color: Math.abs(cell?.value ?? 0) > 0.5 ? 'white' : 'black',
                                    borderLeft: dividerCol !== null && colIdx === dividerCol ? '2px solid #001E2B' : undefined,
                                  }}
                                  title={`${(columnDisplayNames[rowVar] || rowVar.replace(/_/g, ' '))} vs ${(columnDisplayNames[colVar] || colVar.replace(/_/g, ' '))}: ${(cell?.value ?? 0).toFixed(2)}`}
                                >
                                  {(cell?.value ?? 0).toFixed(2)}
                                </div>
                              );
                            })}
                          </div>
                        ))}
                      </div>
                      </div>

                      <div style={{ width: `${matrixWidthPx}px`, maxWidth: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <div
                          style={{
                            width: '100%',
                            height: '1.5rem',
                            background: 'linear-gradient(to right, #DB3030, #FFFFFF, #00ED64)',
                            borderRadius: '0.375rem',
                            marginTop: '1rem',
                            border: '1px solid #E8EDEB',
                          }}
                        ></div>
                        <div style={{ width: '100%', display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem', color: '#3D4F58', marginTop: '0.25rem' }}>
                          <span>-1.0</span>
                          <span>0.0</span>
                          <span>+1.0</span>
                        </div>
                        <div className="flex items-center gap-4 mt-2" style={{ width: '100%', justifyContent: 'center', display: 'flex' }}>
                          <div className="flex items-center gap-2">
                            <div className="w-4 h-4" style={{ backgroundColor: '#DB3030' }}></div>
                            <span className="text-sm text-mongodb-gray-dark3">Negative Correlation</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-4 h-4" style={{ backgroundColor: '#00ED64' }}></div>
                            <span className="text-sm text-mongodb-gray-dark3">Positive Correlation</span>
                          </div>
                        </div>
                      </div>
                    </>
                  ) : null}
                </div>
              );
            })()}
          </CardContent>
        </Card>
      </Slide>

      {/* Slide 5: Channel Contribution Over Time + MMM Breakdown (kept at the bottom) */}
      <Slide title="Channel Contribution Over Time" subtitle="Baseline, media, seasonality, LinkedIn Organic vs macro headwinds">
        <>
            {/* 5.1: Contribution Stacked Area Chart — reframed around macro vs media story */}
            <Card className="mb-6">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="text-sm text-mongodb-gray-dark2 italic max-w-3xl">
                    Positive bands (baseline, media, seasonality, LinkedIn Organic) stack upward; macro headwinds (inflation & unemployment) sit below the axis.
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => openInfoPopup('channelContribution', 'Channel Contribution - Business Problem')}
                      className="p-2 text-mongodb-green-dark2 hover:bg-mongodb-green-dark2 hover:bg-opacity-10 rounded-full transition-colors"
                      title="Learn about the business problem this solves"
                    >
                      <Info className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="h-96">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={contributionData} margin={{ left: 60, right: 20, top: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="date" 
                        tickFormatter={(date) => {
                          const d = new Date(date);
                          return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
                        }}
                      />
                      <YAxis label={{ value: 'Contribution to PT count (%)', angle: -90, position: 'insideLeft' }} />
                      <Tooltip 
                        content={({ active, payload, label }) => {
                          if (active && payload && payload.length) {
                            const lift = payload.find((p) => p.dataKey === 'linkedin_company_page_lift');
                            const drag = payload.find((p) => p.dataKey === 'linkedin_company_page_drag');
                            const companyPageNet =
                              (Number(lift?.value) || 0) + (Number(drag?.value) || 0);
                            const displayItems = payload
                              .filter(
                                (p) =>
                                  p.dataKey !== 'linkedin_company_page_lift' &&
                                  p.dataKey !== 'linkedin_company_page_drag'
                              )
                              .map((p) => ({ name: p.name, value: p.value, color: p.color }));
                            if (lift || drag) {
                              displayItems.push({
                                name: 'LinkedIn Organic',
                                value: companyPageNet,
                                color: lift?.color || drag?.color,
                              });
                            }
                            const reversedPayload = [...displayItems].reverse();
                            return (
                              <div className="bg-white p-3 border border-gray-300 rounded shadow-lg">
                                <p className="font-semibold mb-2">{label}</p>
                                {reversedPayload.map((entry, index) => (
                                  <p key={index} style={{ color: entry.color }}>
                                    {entry.name}: {typeof entry.value === 'number' ? entry.value.toFixed(2) : entry.value}%
                                  </p>
                                ))}
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                      <ReferenceLine y={0} stroke="#001E2B" strokeWidth={1} />
                      <Legend />
                      {/* Positive contributions */}
                      <Area type="monotone" dataKey="baseline" stackId="1" stroke={colors[2]} fill={colors[2]} name="Baseline (organic)" />
                      <Area type="monotone" dataKey="media" stackId="1" stroke={colors[0]} fill={colors[0]} name="Media (paid)" />
                      <Area type="monotone" dataKey="seasonality" stackId="1" stroke={colors[1]} fill={colors[1]} name="Seasonality" />
                      <Area type="monotone" dataKey="linkedin_company_page_lift" stackId="1" stroke={colors[4]} fill={colors[4]} name="LinkedIn Organic" />
                      {/* Macro headwind (negative band) */}
                      <Area type="monotone" dataKey="linkedin_company_page_drag" stackId="2" stroke={colors[4]} fill={colors[4]} name="LinkedIn Organic" legendType="none" />
                      <Area type="monotone" dataKey="inflation_unemployment" stackId="2" stroke={colors[3]} fill={colors[3]} name="Macro headwinds (inflation + unemployment)" />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* MMM Component Equations Table — bottom of the dashboard per Tom's feedback */}
            <Card className="mt-6">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-mongodb-gray-dark3">MMM Component Breakdown Equations</CardTitle>
                  <button
                    onClick={() => setIsExampleModalOpen(true)}
                    className="bg-mongodb-green-dark2 text-white px-4 py-2 rounded-lg shadow-md hover:bg-mongodb-green-dark1 transition-colors flex items-center gap-2 text-sm font-medium"
                    title="View example breakdown"
                  >
                    <Info className="w-4 h-4" />
                    View Example
                  </button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="bg-mongodb-green-dark1 bg-opacity-5 border-l-4 border-mongodb-green-dark2 p-4 rounded mb-4">
                  <h4 className="font-bold text-mongodb-gray-dark3 mb-2">Complete MMM Equation:</h4>
                  <p className="text-mongodb-gray-dark3 font-mono text-sm bg-white p-3 rounded border border-gray-200">
                    Total PTs = Baseline + Media Effects + Macro Economic Factors + Seasonality + Error
                  </p>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full border-collapse border border-gray-200 rounded-lg overflow-hidden">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="border border-gray-200 p-3 text-left font-semibold text-mongodb-gray-dark3">Component</th>
                        <th className="border border-gray-200 p-3 text-left font-semibold text-mongodb-gray-dark3">Formula</th>
                        <th className="border border-gray-200 p-3 text-left font-semibold text-mongodb-gray-dark3">What It Measures</th>
                        <th className="border border-gray-200 p-3 text-left font-semibold text-mongodb-gray-dark3">Concrete Example</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="bg-white hover:bg-mongodb-gray-light3">
                        <td className="border border-gray-200 p-3 font-bold text-mongodb-gray-dark3">Baseline</td>
                        <td className="border border-gray-200 p-3 font-mono text-sm text-gray-700">β₀ = Intercept + Trend</td>
                        <td className="border border-gray-200 p-3 text-gray-700">Your organic business strength - PTs without any marketing spend or external economic factors</td>
                        <td className="border border-gray-200 p-3 text-gray-700">PT's regardless of ad spend (Organic, direct, etc).</td>
                      </tr>
                      <tr className="bg-mongodb-gray-light3 hover:bg-mongodb-gray-light3">
                        <td className="border border-gray-200 p-3 font-bold text-mongodb-gray-dark3">Media Effects</td>
                        <td className="border border-gray-200 p-3 font-mono text-sm text-gray-700">β₁×Google + β₂×Bing + β₃×LinkedIn + β₄×Facebook + β₅×GDN</td>
                        <td className="border border-gray-200 p-3 text-gray-700">Incremental PTs driven by marketing channels</td>
                        <td className="border border-gray-200 p-3 text-gray-700">Media coefficient 0.39 — every $1 spent on Media yields 0.39% in incremental PTs</td>
                      </tr>
                      <tr className="bg-white hover:bg-mongodb-gray-light3">
                        <td className="border border-gray-200 p-3 font-bold text-mongodb-gray-dark3">LinkedIn Organic</td>
                        <td className="border border-gray-200 p-3 font-mono text-sm text-gray-700">β×Engagements + β×Views + β×Follows</td>
                        <td className="border border-gray-200 p-3 text-gray-700">Organic LinkedIn activity contribution to PTs</td>
                        <td className="border border-gray-200 p-3 text-gray-700">Page views lift PTs; engagements and follows may drag when activity is low-quality</td>
                      </tr>
                      <tr className="bg-mongodb-gray-light3 hover:bg-mongodb-gray-light3">
                        <td className="border border-gray-200 p-3 font-bold text-mongodb-gray-dark3">Macro Economic Factors</td>
                        <td className="border border-gray-200 p-3 font-mono text-sm text-gray-700">γ₁×Inflation + γ₂×Unemployment</td>
                        <td className="border border-gray-200 p-3 text-gray-700">Non-marketing business drivers that affect PTs</td>
                        <td className="border border-gray-200 p-3 text-gray-700">+1% CPI & Unemployment Rate increase → -0.41% PTs (economic factors reduce purchasing power)</td>
                      </tr>
                      <tr className="bg-mongodb-gray-light3 hover:bg-mongodb-gray-light3">
                        <td className="border border-gray-200 p-3 font-bold text-mongodb-gray-dark3">Seasonality</td>
                        <td className="border border-gray-200 p-3 font-mono text-sm text-gray-700">Fourier series transformation</td>
                        <td className="border border-gray-200 p-3 text-gray-700">Predictable cyclical patterns</td>
                        <td className="border border-gray-200 p-3 text-gray-700">Black Friday → +40% PTs spike; Q1 ramp-up → +15% lift</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="mt-4 text-sm text-gray-600 italic">
                  <p>Note: Each component's contribution is calculated from the model's posterior distributions and transformed to actual PT units.</p>
                </div>
              </CardContent>
            </Card>
          </>
      </Slide>

      {/* Info Popup for Business Problems */}
      <InfoPopup
        isOpen={infoPopup.isOpen}
        onClose={closeInfoPopup}
        title={infoPopup.title}
        content={infoPopup.content}
      />
      
      {/* Example Table Modal */}
      <ExampleTableModal
        isOpen={isExampleModalOpen}
        onClose={() => setIsExampleModalOpen(false)}
      />
      </div>
    </div>
  );
};

export default MMMMPresentation;
